import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';
import { CameraService } from '../document-kyc-upload-modal/services/camera.service';
import { DocumentUploadModalComponent } from '../document-upload-modal/document-upload-modal.component';
import { CameraCaptureModalComponent, CameraCaptureResult } from '../camera-capture-modal/camera-capture-modal.component';
import { QuestionContext } from '../question-multi-file/question-multi-file.component';
import { StorageService } from 'src/app/core/services/storage.service';

interface FileItem {
  id: string;
  type: 'photo' | 'file';
  value: string; // S3 path (used for saving)
  displayValue?: string; // Public URL for preview
  loadingUrl?: boolean; // Loading state for URL
  name?: string;
}

@Component({
  selector: 'app-question-multi-camera-file',
  templateUrl: './question-multi-camera-file.component.html',
  styleUrls: ['./question-multi-camera-file.component.scss'],
})
export class QuestionMultiCameraFileComponent implements OnInit, OnDestroy {
  @Input() question!: Question;
  @Input() context!: QuestionContext;
  @Output() changeAnswer: EventEmitter<QuestionAnswer> = new EventEmitter();

  // Camera properties
  facingMode: 'user' | 'environment' = 'environment';
  hasMultipleCameras = false;
  isLoading = false;

  // Files/Photos list
  items: FileItem[] = [];
  maxFiles: number = 10;

  private readonly allowedDocumentTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'application/xml',
    'text/xml',
    'application/json',
  ];

  private readonly allowedImageTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/svg+xml',
    'image/avif',
  ];

  private readonly acceptedExtensions = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.csv',
    '.rtf',
    '.odt',
    '.ods',
    '.odp',
    '.xml',
    '.json',
    '.jpg',
    '.jpeg',
    '.jpe',
    '.jfif',
    '.png',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.webp',
    '.heic',
    '.heif',
    '.svg',
    '.avif',
  ];

  private readonly acceptedFormatsLabel =
    'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, RTF, ODT, ODS, ODP, XML, JSON, JPG, JPEG, PNG, GIF, BMP, TIFF, WEBP, HEIC, HEIF, SVG, AVIF';

  constructor(
    private cameraService: CameraService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.checkCameraAvailability();
    
    // Configurar max files
    const limit = this.question.configuration?.limit;
    if (typeof limit === 'number' && limit > 0) {
      this.maxFiles = limit;
    }
    
    // Carregar items existentes
    if (this.question.answer?.value) {
      const value = this.question.answer.value;
      if (Array.isArray(value)) {
        const mappedItems: FileItem[] = [];
        value.forEach((val, index) => {
          if (typeof val === 'string') {
            if (val.startsWith('data:image')) {
              // Base64 image - use directly
              mappedItems.push({
                id: `photo-${index}-${Date.now()}`,
                type: 'photo',
                value: val,
                displayValue: val,
              });
            } else {
              // S3 path - determine type based on extension
              const isImage = this.isImageFile(val);
              mappedItems.push({
                id: `${isImage ? 'photo' : 'file'}-${index}-${Date.now()}`,
                type: isImage ? 'photo' : 'file',
                value: val,
                name: this.extractFileName(val),
              });
            }
          }
        });
        this.items = mappedItems;
        // Load display URLs for all items
        this.loadDisplayUrls();
      }
    }
    
    // Emit initial answer to ensure validation state is consistent
    // This is important to properly handle required field validation
    this.emitAnswer();
  }

  ngOnDestroy(): void {
    // No longer need to stop camera here since it's handled by modal
  }

  async checkCameraAvailability(): Promise<void> {
    const capabilities = await this.cameraService.checkCameraAvailability();
    this.hasMultipleCameras = capabilities.cameras.length > 1;
  }

  get canAddMore(): boolean {
    return this.items.length < this.maxFiles;
  }

  // Camera methods - now using modal
  openCameraModal(): void {
    if (!this.canAddMore) {
      this.snackBar.open(
        `Limite máximo de ${this.maxFiles} arquivo(s) atingido.`,
        'Fechar',
        { duration: 3000 }
      );
      return;
    }

    const dialogRef = this.dialog.open(CameraCaptureModalComponent, {
      data: {
        bucket: this.question.configuration?.bucket || 'default',
        name: this.question.slug,
        ref: this.context.claim_id!,
        question_id: this.question.id,
        facingMode: this.facingMode,
        hasMultipleCameras: this.hasMultipleCameras,
      },
      width: '90vw',
      maxWidth: '600px',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: ['camera-modal-dialog', 'no-padding-dialog'],
      hasBackdrop: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async (result: CameraCaptureResult) => {
      if (result && result.status === 'COMPLETED' && result.value) {
        // Photo was captured and uploaded - add to items
        const newItem: FileItem = {
          id: `photo-${Date.now()}`,
          type: 'photo',
          value: result.value, // S3 path
        };
        
        this.items.push(newItem);
        
        // Load display URL for the new item
        await this.loadDisplayUrlForItem(newItem);
        
        this.emitAnswer();
        
        this.snackBar.open('Foto capturada e enviada com sucesso!', 'Fechar', { 
          duration: 2000 
        });
      }
    });
  }

  // File upload methods
  openUploadModal(): void {
    if (!this.canAddMore) {
      this.snackBar.open(
        `Maximum of ${this.maxFiles} file(s) reached.`,
        'Close',
        { duration: 3000 }
      );
      return;
    }

    const remainingSlots = this.maxFiles - this.items.length;
    
    const dialogData = {
      bucket: this.question.configuration?.bucket || 'default',
      name: this.question.slug,
      multiple: remainingSlots > 1,
      ref: this.context.claim_id!,
      question_id: this.question.id,
      type: 'CLAIM' as const,
      maxFileSize: this.question.configuration?.maxFileSize,
      maxFiles: remainingSlots,
      allowedTypes: [...this.allowedDocumentTypes, ...this.allowedImageTypes],
      accept: this.acceptedExtensions.join(','),
      acceptedFormatsLabel: this.acceptedFormatsLabel,
    };

    const dialogRef = this.dialog.open(DocumentUploadModalComponent, {
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(filter((e) => !!e))
      .subscribe(async (result) => {
        const parsed = JSON.parse(result);
        if (parsed.status === 'COMPLETED' && parsed.value && parsed.value.length > 0) {
          const newItems: FileItem[] = parsed.value.map((url: string, index: number) => {
            const isImage = this.isImageFile(url);
            return {
              id: `${isImage ? 'photo' : 'file'}-${Date.now()}-${index}`,
              type: isImage ? 'photo' : 'file',
              value: url,
              name: this.extractFileName(url),
            };
          });
          
          this.items.push(...newItems);
          
          // Load display URLs for all new items
          await this.loadDisplayUrls();
          
          this.emitAnswer();
          
          this.snackBar.open(
            `${newItems.length} file(s) uploaded successfully!`,
            'Close',
            { duration: 2000 }
          );
        }
      });
  }

  removeItem(itemId: string): void {
    this.items = this.items.filter(item => item.id !== itemId);
    this.emitAnswer();
    
    this.snackBar.open('Item removed.', 'Close', { duration: 2000 });
  }

  private extractFileName(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      return parts[parts.length - 1] || 'arquivo';
    } catch {
      // If URL parsing fails, try to extract from path directly
      const parts = url.split('/');
      return parts[parts.length - 1] || 'arquivo';
    }
  }

  /**
   * Check if a file is an image based on its extension
   */
  private isImageFile(path: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.heic', '.heif', '.avif', '.tiff', '.tif'];
    const lowerPath = path.toLowerCase();
    return imageExtensions.some(ext => lowerPath.endsWith(ext));
  }

  /**
   * Load display URLs for all items that don't have one yet
   */
  private async loadDisplayUrls(): Promise<void> {
    const bucket = this.question.configuration?.bucket || 'claim-documents';
    
    for (const item of this.items) {
      if (!item.displayValue && !item.value.startsWith('data:')) {
        await this.loadDisplayUrlForItem(item, bucket);
      }
    }
  }

  /**
   * Load display URL for a single item
   */
  private async loadDisplayUrlForItem(item: FileItem, bucket?: string): Promise<void> {
    if (item.displayValue || item.value.startsWith('data:')) {
      return; // Already has a display value or is base64
    }
    
    const bucketName = bucket || this.question.configuration?.bucket || 'claim-documents';
    
    try {
      item.loadingUrl = true;
      const { url, error } = await this.storageService.getFileUrl(bucketName, item.value);
      
      if (url) {
        item.displayValue = url;
      } else {
        console.error('Failed to load display URL for:', item.value, error);
        // Keep the original value as fallback
        item.displayValue = item.value;
      }
    } catch (error) {
      console.error('Error loading display URL:', error);
      item.displayValue = item.value;
    } finally {
      item.loadingUrl = false;
    }
  }

  private emitAnswer(): void {
    // All items now contain S3 paths (no more base64 pending uploads)
    const values = this.items.map(item => item.value);
    
    // Validate based on whether question is required (elective = false means required)
    const isRequired = this.question.elective === false;
    const hasValues = values.length > 0;
    
    // For required questions, isValid is true only if there are items
    // For optional questions, isValid is always true
    this.question.isValid = isRequired ? hasValues : true;
    
    const answer: QuestionAnswer = {
      question: this.question,
      answer: {
        value: hasValues ? values as any : null,
        isIgnored: false,
      },
    };
    this.changeAnswer.emit(answer);
  }
}
